import React from 'react';
import {
  IconCalendarCheck,
  IconMoonStars,
  IconSun,
} from '@tabler/icons-react';

const AttendanceOverview = ({
  dayShiftworkingDays = 0,
  nightShiftworkingDays = 0,
  leaveDays = 0,
  attendancePercentage = '0%',
  filters,
}) => {
  const percent = parseFloat(attendancePercentage.replace('%', '')) || 0;
  const attendanceRatio = percent / 100;
  const circumference = 2 * Math.PI * 50;
  const offset = circumference * (1 - attendanceRatio);

  // Tag and badge colors
  const getTagColorClass = () => {
    if (percent <= 40) return 'text-red-800 bg-red-200';
    if (percent <= 70) return 'text-yellow-800 bg-yellow-200';
    return 'text-green-800 bg-green-200';
  };

  const getTagText = () => {
    if (percent <= 40) return '🔴 Needs Improvement';
    if (percent <= 70) return '⚠️ Fair Attendance';
    return '✅ Excellent Attendance';
  };

  // Circle stroke color
  const getStrokeColor = () => {
    if (percent <= 40) return '#dc2626'; // red
    if (percent <= 70) return '#facc15'; // yellow
    return '#16a34a'; // green
  };

  const cardContainer = `
    bg-white relative p-6 px-8 text-center
    border border-[#CBD5E1] rounded-3xl
    shadow-[0_10px_35px_rgba(0,0,0,0.05)]
    flex flex-col items-center justify-between
    transition-transform duration-300
    hover:scale-[1.035] hover:shadow-xl
    overflow-hidden
  `;

  const iconWrapper = `
    w-14 h-14 mb-4 flex items-center justify-center 
    rounded-full shadow-md border border-white/30
  `;

  const topBorder = (
    <div className="absolute top-0 left-0 w-full h-[10px] bg-gradient-to-r from-[#EE1D23] via-pink-400 to-[#1A2B5C] rounded-t-3xl z-10" />
  );

  const cardBgBlur = (
    <>
      <div className="absolute -top-10 -left-10 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl opacity-20 z-0" />
      <div className="absolute -bottom-10 -right-10 w-28 h-28 bg-red-200/30 rounded-full blur-2xl opacity-30 z-0" />
    </>
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 px-2">
      {/* Day Shift Working Days */}
      <div className={cardContainer}>
        {topBorder}
        {cardBgBlur}
        <h3 className="text-lg font-bold text-[#0f172a] mb-4">🕘 Day Shift Working Days</h3>
        <div className={`${iconWrapper} bg-yellow-100 z-10`}>
          <IconSun size={28} className="text-yellow-700" />
        </div>
        <div className="text-4xl font-extrabold text-yellow-700 z-10">{dayShiftworkingDays}</div>
        <p className="text-sm text-black mt-2 z-10">This Month</p>
      </div>

      {/* Night Shift Working Days */}
      <div className={cardContainer}>
        {topBorder}
        {cardBgBlur}
        <h3 className="text-lg font-bold text-[#0f172a] mb-4">🌙 Night Shift Working Days</h3>
        <div className={`${iconWrapper} bg-indigo-100 z-10`}>
          <IconMoonStars size={28} className="text-indigo-700" />
        </div>
        <div className="text-4xl font-extrabold text-indigo-700 z-10">{nightShiftworkingDays}</div>
        <p className="text-sm text-black mt-2 z-10">This Month</p>
      </div>

      {/* Total Leave Days */}
      <div className={cardContainer}>
        {topBorder}
        {cardBgBlur}
        <h3 className="text-lg font-bold text-[#0f172a] mb-4">🛌 Total Leave Days</h3>
        <div className={`${iconWrapper} bg-red-100 z-10`}>
          <IconCalendarCheck size={28} className="text-red-700" />
        </div>
        <div className="text-4xl font-extrabold text-red-700 z-10">{leaveDays}</div>
        <p className="text-sm text-black mt-2 z-10">This Month</p>
      </div>

      {/* Attendance Percentage */}
      <div className={cardContainer}>
        {topBorder}
        {cardBgBlur}
        <h3 className="text-lg font-bold text-[#0f172a] mb-4 z-10">🎯 Attendance Percentage</h3>
        <div className="relative w-32 h-32 mb-4 z-10">
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="50" stroke="#e2e8f0" strokeWidth="10" fill="none" />
            <circle
              cx="60"
              cy="60"
              r="50"
              stroke={getStrokeColor()}
              strokeWidth="10"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-700"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-2xl font-extrabold text-[#0f172a]">
            {Math.round(percent)}%
          </div>
        </div>
        <p className="text-sm text-black mb-2 z-10">
          Overall Monthly Attendance
        </p>
        <span className={`${getTagColorClass()} text-sm font-semibold px-4 py-1 rounded-full z-10`}>
          {getTagText()}
        </span>
      </div>
    </div>
  );
};

export default AttendanceOverview;
